import { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { Input, Select } from "../ui/Field";
import { Drawer } from "../ui/Overlay";
import { Tag } from "../ui/Tag";
import { DataTable, type Column } from "../data/DataTable";
import { DateTimeText } from "../ui/DateTimeField";
import { fetchAudit, type AuditEntry } from "../../lib/api";
import { useT, type MsgKey } from "../../lib/i18n";
import type { Change } from "../../lib/autosave";
import { useChangeLabels } from "../version/changeLabels";
import { normalizeLegacy } from "../../lib/version-log";
import { describeAction, targetLabel } from "../../lib/audit-label";

/**
 * 操作日志。
 *
 * 动作名走 i18n（`audit.<action>`），拿不到翻译时**回落显示原始 action 串** ——
 * 新加的动作类型来不及补词条时，至少还能看出发生过什么，
 * 比显示成空白或 `audit.xxx` 强。
 */
export function AuditDrawer({ onClose, version, target, title, actor: initActor }: {
  /** 打开时预填「按操作人筛选」：从修改记录「在操作日志里看某人」过来 */
  actor?: string;
  onClose: () => void;
  /**
   * 只看某一个配置版本上发生的事。给「谁建的这个草稿、谁改过什么」用——
   * 草稿现在是全代理共用的一份，进来第一眼就得看得见别人动过哪儿（leo 2026-09-09）。
   */
  version?: number;
  /** 只看某一条对象的修改记录（精确匹配 target，自动留痕时是请求路径），如物品资料的单行审计 */
  target?: string;
  title?: string;
}) {
  const t = useT();
  const { where, field, side, value } = useChangeLabels();
  const [actor, setActor] = useState(initActor ?? "");
  // 版本的「修改记录」是另一个抽屉（VersionLog），这里是完整日志，默认看全部
  const [action, setAction] = useState("");
  const [nonce, setNonce] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ total: number; items: AuditEntry[] } | null>(null);
  const [state, setState] = useState<"loading" | "idle" | "error">("loading");

  useEffect(() => {
    let alive = true;
    setState("loading");
    const timer = setTimeout(() => {
      fetchAudit({ page, actor: actor.trim() || undefined, action: action || undefined, version, target })
        .then((d) => { if (alive) { setData(d); setState("idle"); } })
        .catch(() => { if (alive) setState("error"); });
    }, actor ? 300 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [page, actor, action, nonce, version, target]);

  /** 下拉里的候选动作。新动作没进这张表也不影响——筛选框留空即可看全部 */
  const KNOWN_ACTIONS = [
    "config.autosave", "config.verDraft", "config.verPublish", "config.verRollback",
    "agent.switch", "session.logout",
  ];

  /**
   * 动作名三种语言都要能读：点名记的动作查 `audit.<action>`，写接口统一留痕的「方法 + 路由」
   * 拆成「保存 · spin 奖励配置」（见 lib/audit-label.ts）。认不出的回落原始 action 串——
   * 新动作来不及补词条时至少还能看出发生过什么，比空白强；漏登记的写接口有用例拦。
   *
   * ⚠ 别在注释里把动态前缀原样写成模板字面量：i18n 孤儿键扫描器不剥注释，写了等于把闸关上。
   */
  const actionLabel = (a: string) => {
    const d = describeAction(a, (k) => t(k as MsgKey) !== k);
    if (!d) return a;
    if (d.kind === "named") return d.rest ? `${t(d.key)} · ${d.rest}` : t(d.key);
    return `${t(d.verb)} · ${d.res ? t(d.res) : d.raw}`;
  };

  /** 几种常见动作的详情写成一句话；其余照旧贴 JSON（字段是接口自己定的，逐个翻译不划算） */
  const detailText = (r: AuditEntry): string | null => {
    const d = r.detail as Record<string, unknown>;
    const v = (x: unknown) => String(x);
    switch (r.action) {
      case "config.verPublish": return d.replaced ? t("audit.d.replaced", { v: v(d.replaced) }) : t("audit.d.firstLive");
      case "config.verDraft": return d.from ? t("audit.d.from", { v: v(d.from) }) : null;
      case "config.verRollback": return d.from ? t("audit.d.rollbackTo", { v: v(d.from) }) : null;
      case "agent.switch": return d.from ? t("audit.d.agentFrom", { a: v(d.from) }) : null;
      default: return null;
    }
  };

  /**
   * 列宽按内容**实际需要**定，配 fixedLayout 才守得住。
   * 之前用 auto 布局，时间列被挤到 81px、换行成三行，行高涨到 81px；
   * 而 IP 白占了 298px。
   */
  const columns: Column<AuditEntry>[] = [
    {
      key: "at", title: t("log.at"), width: 196,
      // 时间必须单行：换行会把整行撑高，一屏看不了几条
      render: (r) => <span className="whitespace-nowrap text-ink-regular"><DateTimeText value={r.atMs} withSeconds /></span>,
    },
    {
      key: "actor", title: t("log.who"), width: 120,
      render: (r) => <span className="block truncate" title={r.actor}>{r.actor || "—"}</span>,
    },
    { key: "a0", title: t("log.agent"), width: 76, render: (r) => <span className="tabular-nums">{r.a0 || "—"}</span> },
    {
      key: "action", title: t("log.action"), width: 180,
      render: (r) => <Tag tone="primary">{actionLabel(r.action)}</Tag>,
    },
    {
      key: "target", title: t("log.target"), width: 110,
      // 写接口留痕的 target 是整条请求路径，只显示里面的 id；悬停仍看全路径
      render: (r) => <span className="block truncate font-mono text-xs" title={r.target}>{targetLabel(r.action, r.target) ?? "—"}</span>,
    },
    {
      key: "detail", title: t("log.detail"),
      render: (r) => {
        if (!r.detail) return <span className="text-ink-placeholder">—</span>;
        const changes = (r.detail as { changes?: Change[] }).changes;
        if (Array.isArray(changes)) {
          return (
            <ul className="space-y-0.5 text-xs">
              {changes.map(normalizeLegacy).map((c, i) => (
                <li key={i} className="break-all">
                  <span className="text-ink-regular">{where(c)} · {field(c)}</span>
                  {/* 整行新增 / 删除：位置里已经写了行名、字段写了「新增这一行」，不再贴整行 JSON */}
                  {!c.op && <>
                    {"："}
                    <span className="text-ink-placeholder line-through">{value(side(c, "from"), c.row)}</span>
                    {" → "}
                    <b>{value(side(c, "to"), c.row)}</b>
                  </>}
                </li>
              ))}
              {(r.detail as { truncated?: boolean }).truncated && <li className="text-ink-placeholder">{t("log.truncated")}</li>}
            </ul>
          );
        }
        const text = detailText(r);
        if (text) return <span className="text-xs text-ink-regular">{text}</span>;
        const json = JSON.stringify(r.detail);
        return (
          <span className="block truncate font-mono text-xs text-ink-secondary" title={json}>{json}</span>
        );
      },
    },
    {
      key: "ip", title: t("log.ip"), width: 140,
      render: (r) => <span className="block truncate font-mono text-xs text-ink-secondary" title={r.ip}>{r.ip || "—"}</span>,
    },
  ];

  const pages = data ? Math.ceil(data.total / 20) : 1;

  return (
    <Drawer
      title={title ?? t("log.title")}
      width={1040}
      onClose={onClose}
      extra={<Button onClick={onClose}>{t("log.close")}</Button>}
    >
      <div className="mb-3 flex items-center gap-3">
        <Input value={actor} onChange={(v) => { setActor(v); setPage(1); }} placeholder={t("log.searchActor")} className="w-[180px]" />
        <Select value={action} className="w-[170px]" onChange={(v) => { setAction(v); setPage(1); }}>
          <option value="">{t("log.allActions")}</option>
          {KNOWN_ACTIONS.map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}
        </Select>
        <Button size="sm" onClick={() => setNonce((n) => n + 1)}>{t("log.refresh")}</Button>
        {data && data.total > 0 && (
          <span className="text-xs text-ink-placeholder">
            {t("log.range", { from: (page - 1) * 20 + 1, to: Math.min(page * 20, data.total) })} · {t("log.total", { n: data.total })}
          </span>
        )}
        {pages > 1 && (
          <span className="ml-auto flex items-center gap-2">
            <Button size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>{t("levelPicker.prev")}</Button>
            <span className="text-sm text-ink-secondary">{page} / {pages}</span>
            <Button size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>{t("levelPicker.next")}</Button>
          </span>
        )}
      </div>

      {state === "error" ? (
        <div className="rounded-base border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {t("agent.loadFailed")}
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={state === "loading" ? [] : (data?.items ?? [])}
          rowKey={(r) => r.id}
          minWidth={940}
          fixedLayout
          empty={
            state === "loading" ? (
              t("agent.searching")
            ) : (
              <div className="py-6">
                <div className="text-sm text-ink-regular">{t("log.empty")}</div>
                <div className="mt-1.5 text-xs text-ink-placeholder">{t("log.emptyHint")}</div>
              </div>
            )
          }
        />
      )}
    </Drawer>
  );
}
